/**
 * Daily-caps matrix API wrapper (v2 — Project Independence, DNKADM-0057 U1).
 *
 * A **scope-keyed matrix**, NOT the v1 tiers×buckets grid. Each `v2_daily_cap` row
 * caps the hours/day a booking may consume, keyed by a derived `scope` over an
 * optional membership PLAN and an optional credit BUCKET (handoff §5.11):
 *
 *  - `scope` (`ALL|PLAN|BUCKET|PLAN_BUCKET`) — **derived**, never picked directly;
 *  - `membershipPlanId` (a 0056 plan id, or `null` = all plans);
 *  - `creditBucketId` (a 0052 first-class bucket id, or `null` = all buckets);
 *  - `capHours` (`DECIMAL(5,1)`).
 *
 * Unique on `(tenant, scope, plan, bucket)` — the create/update `409` surfaces. A
 * booking matches the **single most-specific** row: `PLAN_BUCKET > PLAN > BUCKET >
 * ALL`; **no matching row = uncapped**. This is the ONLY daily-cap mechanism now —
 * the v1 global `dailySchedulingCapHours` setting is gone (§9.5; the v1 tiers×buckets
 * grid this module used is retired).
 *
 * **`scope` is DERIVED here, not stored by the admin.** {@link deriveScope} computes
 * it from the presence/absence of `membershipPlanId`/`creditBucketId`, so the UI only
 * offers "applies to: all / a plan / a bucket / a plan+bucket" + a cap-hours value.
 * The derived scope is sent on the body alongside the two ids (the server may also
 * re-derive; sending it keeps the contract explicit).
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.11, §3.1 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/daily-caps` → {@link DailyCap}`[]`.
 *  - `POST   /v2/admin/daily-caps` ← {@link DailyCapInput} → the created row.
 *  - `PUT    /v2/admin/daily-caps/{id}` ← {@link DailyCapInput} → the updated row.
 *  - `DELETE /v2/admin/daily-caps/{id}` (soft-delete).
 *
 * Reads are parsed DEFENSIVELY (mirrors `services.ts` / `creditBuckets.ts`): a
 * missing/wrong-typed field degrades (an id-less row is dropped), never a throw. The
 * list array is extracted tolerantly (bare array or a `dailyCaps`/`content` wrapper).
 */
import { request } from './client';

/** v2 admin daily-caps base path (bearer surface). */
const PATH = '/v2/admin/daily-caps';

/**
 * A daily-cap scope (handoff §5.11). DERIVED from which of `membershipPlanId` /
 * `creditBucketId` are set — never picked directly by the admin. Most-specific
 * wins at booking time: `PLAN_BUCKET > PLAN > BUCKET > ALL`.
 */
export type DailyCapScope = 'ALL' | 'PLAN' | 'BUCKET' | 'PLAN_BUCKET';

/**
 * One `v2_daily_cap` row (§5.11). `membershipPlanId`/`creditBucketId` are `null` when
 * the cap is not scoped to that axis (`null,null` = the `ALL` row). `scope` is the
 * derived key; `capHours` is a non-negative `DECIMAL(5,1)`.
 */
export interface DailyCap {
  id: string;
  /** The derived scope key (`ALL|PLAN|BUCKET|PLAN_BUCKET`). */
  scope: DailyCapScope;
  /** The scoped membership-plan id (0056), or `null` for "all plans". */
  membershipPlanId: string | null;
  /** The scoped credit-bucket id (0052), or `null` for "all buckets". */
  creditBucketId: string | null;
  /** Max hours/day (`DECIMAL(5,1)`, ≥ 0). */
  capHours: number;
}

/**
 * Create/update body for a daily cap. The form supplies the two optional ids + the
 * cap hours; `scope` is DERIVED from the ids by {@link deriveScope} (the admin never
 * picks it). `id` is never in the body — an edit keys on the path segment.
 */
export interface DailyCapInput {
  /** The scoped plan id, or `null` for "all plans". */
  membershipPlanId: string | null;
  /** The scoped bucket id, or `null` for "all buckets". */
  creditBucketId: string | null;
  /** Max hours/day (`DECIMAL(5,1)`, ≥ 0). */
  capHours: number;
}

/**
 * Derive the `scope` key from which axes are set — the single source of truth for
 * the cap's scope (the admin picks plan/bucket; we compute scope):
 *  - plan + bucket → `PLAN_BUCKET`
 *  - plan only     → `PLAN`
 *  - bucket only   → `BUCKET`
 *  - neither       → `ALL`
 */
export function deriveScope(
  membershipPlanId: string | null,
  creditBucketId: string | null,
): DailyCapScope {
  const hasPlan = membershipPlanId !== null;
  const hasBucket = creditBucketId !== null;
  if (hasPlan && hasBucket) return 'PLAN_BUCKET';
  if (hasPlan) return 'PLAN';
  if (hasBucket) return 'BUCKET';
  return 'ALL';
}

// --- Defensive parsing helpers (mirrors services.ts / creditBuckets.ts) ---------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI keys off — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized as text), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse one raw daily-cap row, or null when it has no usable `id` (an id-less row
 * can't be rendered or edited — drop it rather than surface a half-identified cap).
 * `membershipPlanId`/`creditBucketId` are null when absent/blank. `scope` is RE-DERIVED
 * from the parsed ids (we trust the ids over a possibly-stale server `scope`, and a
 * malformed/absent `scope` degrades to the derived value). `capHours` degrades to 0
 * on a malformed read (the server is the authority).
 */
function parseDailyCap(raw: unknown): DailyCap | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;
  // Nested v2 fk ids — numeric on the wire (DNKADM-0064); null = unscoped axis.
  const membershipPlanId = asId(raw['membershipPlanId']);
  const creditBucketId = asId(raw['creditBucketId']);
  return {
    id,
    // Derive from the ids so the rendered scope can never disagree with them.
    scope: deriveScope(membershipPlanId, creditBucketId),
    membershipPlanId,
    creditBucketId,
    capHours: asNumber(raw['capHours']) ?? 0,
  };
}

/**
 * Extract the caps array TOLERANTLY from a successful body, returning `[]` when
 * NEITHER a bare array NOR a known wrapper key (`dailyCaps`/`caps`/`content`) yields
 * one (a missing list is a real first-run state, §8).
 */
function extractCaps(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['dailyCaps'])) return body['dailyCaps'];
    if (Array.isArray(body['caps'])) return body['caps'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/**
 * Build the JSON body for a create/update. Sends the two ids explicitly (including
 * `null`) and the DERIVED `scope` — the admin never picks scope; it is computed from
 * the ids.
 */
function toBody(input: DailyCapInput): Record<string, unknown> {
  return {
    scope: deriveScope(input.membershipPlanId, input.creditBucketId),
    membershipPlanId: input.membershipPlanId,
    creditBucketId: input.creditBucketId,
    capHours: input.capHours,
  };
}

function capPath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/daily-caps` → the tenant's daily-cap rows, defensively parsed (a bad
 * entry dropped). A body that yields no array degrades to `[]` (a real first-run
 * state, §8 — meaning every booking is uncapped). Throws a typed `ApiError` on a
 * non-2xx response.
 */
export async function listDailyCaps(): Promise<DailyCap[]> {
  const body = await request<unknown>('GET', PATH);
  return extractCaps(body)
    .map(parseDailyCap)
    .filter((c): c is DailyCap => c !== null);
}

/**
 * `POST /v2/admin/daily-caps` → create a cap. The body carries the derived `scope`
 * (from the plan/bucket selection) + the two ids + `capHours`. Returns the re-parsed
 * created row. Throws a typed `ApiError` (`400` VALIDATION, `409` CONFLICT on the
 * unique `(tenant, scope, plan, bucket)` rule, upstream).
 */
export async function createDailyCap(input: DailyCapInput): Promise<DailyCap | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parseDailyCap(body);
}

/**
 * `PUT /v2/admin/daily-caps/{id}` → update a cap. Same body as create; the row is
 * keyed by the `id` path segment. Returns the re-parsed updated row. Throws a typed
 * `ApiError` (`400`, `404`, `409`, upstream).
 */
export async function updateDailyCap(
  id: string,
  input: DailyCapInput,
): Promise<DailyCap | null> {
  const body = await request<unknown>('PUT', capPath(id), { json: toBody(input) });
  return parseDailyCap(body);
}

/**
 * `DELETE /v2/admin/daily-caps/{id}` → remove a cap (soft-delete, §4.3). Throws a
 * typed `ApiError` (`404` NOT_FOUND when no such row, `403`, upstream forwarded).
 * Refetch the list after this resolves.
 */
export async function deleteDailyCap(id: string): Promise<void> {
  await request<unknown>('DELETE', capPath(id));
}
